import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, useRoute, useRouter, useStore, watch } from "@nuxtjs/composition-api";
import { type CSearchInput } from "@rscollabs/capsule-wardrobe-components/src/components/CSearchInput";

import { useDebounce, useI18n, useTranslate } from "@/composables";
import { useRotatingSearchSuggestions } from "@/composables/search";
import { useConsumerSearchStore } from "@/store";
import { getAmplitudeStore } from "@/store/amplitude";
import { getExperimentsStore } from "@/store/experiments";
import { getFollowerStore } from "@/store/follower";
import { getSearchStore } from "@/store/search";
import { useSearchAssistanceStore } from "@/store/search-assistance";
import { matchesCaseInsensitive } from "@/utils/comparators/string";
import { SEARCH_SUGGESTIONS_DEBOUNCE_DELAY, SEARCH_SUGGESTIONS_LIMIT } from "@/utils/constants";
import {
  EXPERIMENTS_CONSUMER_SEARCH,
  FEATURE_FLAG_ACTIVE,
  FLAG_CONSUMER_SEARCH_AUTOCOMPLETE,
} from "@/utils/experiments/constants";


export default /*#__PURE__*/_defineComponent({
  __name: 'SearchInput',
  props: {
  isInNav: {
    type: Boolean,
    default: false,
  },
},
  emits: ["search-click", "search-enter", "search-select", "search-update"],
  setup(__props, { expose, emit }: { emit: ({
  (e: "search-click", keyword: string): void;
  (e: "search-enter", { search, select }: SearchEmit): void;
  (e: "search-select", { search, select }: SearchEmit): void;
  (e: "search-update", keyword: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;



const { t } = useTranslate();
const router = useRouter();
const route = useRoute();
const store = useStore();
const i18n = useI18n();
const input = ref<InstanceType<typeof CSearchInput> | null>(null);
const experimentsStore = getExperimentsStore(store);
const searchAssistanceStore = useSearchAssistanceStore();
const consumerSearchStore = useConsumerSearchStore();

const { currentSuggestion } = useRotatingSearchSuggestions();

const numberOfProfilesToFetch = 3;

const searchSuggestions = ref<AutocompleteSuggestions>([]);

const keyword = ref<string | null>("");
const userKeyword = ref("");

const isSearchAssistanceEnabled = computed(
  () => experimentsStore.flag(FLAG_CONSUMER_SEARCH_AUTOCOMPLETE)?.value === FEATURE_FLAG_ACTIVE,
);



const getSearchHistory = (type: SearchType) => getSearchStore(store).historyTypeItem(type);

const recentSearches = computed(() => {
  const results: AutocompleteSuggestions = [];
  const recentSearches = getSearchHistory("post");

  if (recentSearches.length > 0) {
    results.push({ header: t("Recent Searches").value });
    results.push(...recentSearches);
  }

  return results;
});

const popularSearches = computed(() => {
  const results: AutocompleteSuggestions = [
    { header: t("Popular Searches").value },
    ...getSearchStore(store).popularSearches,
  ];

  return results;
});

const recentCreators = computed(() => {
  const results: AutocompleteSuggestions = [];
  const recentCreators = getSearchHistory("profile");

  if (recentCreators.length) {
    results.push({ header: t("Recent Creators").value });
    results.push(...recentCreators);
  }

  return results;
});

const popularCreators = computed(() => {
  const results: AutocompleteSuggestions = [];
  const popularCreators = getFollowerStore(store).recommendedProfilesSuggestions;

  if (popularCreators.length) {
    results.push({ header: t("Popular Creators").value });
    results.push(...popularCreators);
  }

  return results;
});

const suggestionsWithoutKeyword = computed(() => {
  const postSuggestions = recentSearches.value?.length ? recentSearches.value : popularSearches.value;
  const profileSuggestions = recentCreators.value?.length ? recentCreators.value : popularCreators.value;

  return [...postSuggestions, ...profileSuggestions];
});

const relatedSearches = (keyword: string) => {
  const results: AutocompleteSuggestions = [];
  const relatedSearches = getSearchStore(store).formattedSuggestions("suggestion");

  if (relatedSearches.length) {
    results.push({ header: t("Related Searches").value });
    // Check if the keyword is already in the related searches
    const found = relatedSearches.find((item) => item.query === keyword && item.objectID === keyword);
    // If not found, manually add it to the results so the autocomplete can show it
    if (keyword === userKeyword.value && route.value.name === "search" && !found) {
      results.push({ objectID: keyword, query: keyword });
    }
    results.push(...relatedSearches);
  } else {
    results.push({ objectID: keyword, query: keyword });
  }

  return results;
};

const suggestedCreators = computed(() => {
  const results: AutocompleteSuggestions = [];
  const suggestedCreators = getSearchStore(store).formattedSuggestions("profile");

  if (suggestedCreators?.length) {
    results.push({ header: t("Suggested Creators").value });
    results.push(...suggestedCreators);
  }
  return results;
});

const isConsumerSearchEnabled = computed(
  () => experimentsStore.variant(EXPERIMENTS_CONSUMER_SEARCH)?.value === FEATURE_FLAG_ACTIVE,
);
const placeholderText = computed(() => {
  if (!isConsumerSearchEnabled.value) {
    return "";
  }
  return currentSuggestion.value;
});

const suggestionsByKeyword = (keyword: string) => relatedSearches(keyword).concat(suggestedCreators.value);

const getSelectedSearch = ({ select }: SearchEmit, action = "select") => {
  const selectedQuery = select?.query;
  const searchStore = getSearchStore(store);

  // go to profile page if select a profile, otherwise go to search post page
  if (select.avatarUrl) {
    setReferringComponent();
    searchStore.addSearchHistory({ searchItem: select, type: "profile" });
    router.push({ name: "explore-displayName", params: { displayName: selectedQuery } });
  } else {
    if (action === "select") {
      setReferringComponent();
    }

    searchStore.addSearchHistory({ searchItem: select, type: "post" });

    const type = route.value.query.type || "post";
    const debug = route.value.query.debug;
    router.push({
      path: "/search",
      query: { keyword: selectedQuery, type, ...(debug === "true" && { debug }) },
    });
  }
};

const onSearchClick = (keyword: string) => {
  emit("search-click", keyword);
  setReferringComponent();

  if (!keyword) {
    consumerSearchStore.queryType = "recent";
    searchSuggestions.value = suggestionsWithoutKeyword.value;
  } else {
    getSearchSuggestions(keyword);
  }
};

const onSearchEnter = ({ search, select }: SearchEmit) => {
  consumerSearchStore.queryType = "typed";
  emit("search-enter", { search, select });
  if (select?.query) {
    setReferringComponent();
    getSelectedSearch({ search, select }, "enter");
  }
};

const onSearchSelect = ({ search, select }: SearchEmit) => {
  emit("search-select", { search, select });
  if (select?.query) {
    getSelectedSearch({ search, select });
  }
};

const onSearchInput = (keywordParam: string) => {
  emit("search-update", keywordParam);
  if (keywordParam) {
    keyword.value = keywordParam;
    getSearchSuggestionsDebounce(keywordParam);
  } else {
    consumerSearchStore.queryType = "recent";
    searchSuggestions.value = suggestionsWithoutKeyword.value;
  }
};

const setReferringComponent = () => {
  if (props.isInNav) {
    getAmplitudeStore(store).setReferringComponent("site header search field");
  } else {
    getAmplitudeStore(store).setReferringComponent("homepage search field module");
  }
};

const getSearchSuggestionsDebounce = useDebounce((keywordParam: string) => {
  if (matchesCaseInsensitive(keyword.value ?? "", keywordParam, i18n.locale)) {
    keyword.value = keywordParam;
    if (isSearchAssistanceEnabled.value) {
      getSearchAssistanceSuggestions(keywordParam);
    } else {
      getSearchSuggestions(keywordParam);
    }
  }
}, SEARCH_SUGGESTIONS_DEBOUNCE_DELAY);

const getSearchAssistanceSuggestions = async (keyword: string) => {
  await searchAssistanceStore.fetchAutocompleteResults({ q: keyword, limit: SEARCH_SUGGESTIONS_LIMIT });
  const results: AutocompleteSuggestions = [];
  const autocompleteResults = searchAssistanceStore.autocompleteResults.map((result) => ({
    objectID: result,
    query: result,
  }));
  if (autocompleteResults.length) {
    results.push({ header: t("Related Searches").value });
    // Check if the keyword is already in the related searches
    const found = autocompleteResults.find((item) => item.query === keyword && item.objectID === keyword);
    // If not found, manually add it to the results so the autocomplete can show it
    if (keyword === userKeyword.value && route.value.name === "search" && !found) {
      results.push({ objectID: keyword, query: keyword });
    }
    results.push(...autocompleteResults);
  } else {
    results.push({ objectID: keyword, query: keyword });
  }

  consumerSearchStore.queryType = "suggested";
  searchSuggestions.value = results;
};

const getSearchSuggestions = (keyword: string) => {
  if (keyword) {
    getSearchStore(store).queueSearchSuggestions({ query: keyword, types: ["suggestion", "profile"] });
  }
  consumerSearchStore.queryType = "suggested";
  searchSuggestions.value = suggestionsByKeyword(keyword);
};

onMounted(() => {
  experimentsStore.fetchFeatureFlag({ flagName: FLAG_CONSUMER_SEARCH_AUTOCOMPLETE });
  experimentsStore.fetchVariant({ variantName: EXPERIMENTS_CONSUMER_SEARCH });
  getFollowerStore(store).fetchRecommendedProfiles(numberOfProfilesToFetch);
});

watch(
  () => route.value.query.keyword,
  (keywordParam) => {
    if (keywordParam === undefined || Array.isArray(keywordParam)) {
      return;
    }

    // First check if this element is already in our list - if so, we can just use it
    const found = searchSuggestions.value.some((element) =>
      "query" in element ? element.query === keywordParam && element.objectID === keywordParam : false,
    );

    // Not in list, so we need to fake one and add it
    if (found === false) {
      searchSuggestions.value = [{ objectID: keywordParam, query: keywordParam }];
    }
    userKeyword.value = keywordParam;
    keyword.value = keywordParam;
  },
  { immediate: true },
);

expose({
  input,
});

return { __sfc: true,props, t, router, route, store, i18n, input, experimentsStore, searchAssistanceStore, consumerSearchStore, currentSuggestion, numberOfProfilesToFetch, searchSuggestions, keyword, userKeyword, isSearchAssistanceEnabled, emit, getSearchHistory, recentSearches, popularSearches, recentCreators, popularCreators, suggestionsWithoutKeyword, relatedSearches, suggestedCreators, isConsumerSearchEnabled, placeholderText, suggestionsByKeyword, getSelectedSearch, onSearchClick, onSearchEnter, onSearchSelect, onSearchInput, setReferringComponent, getSearchSuggestionsDebounce, getSearchAssistanceSuggestions, getSearchSuggestions }
}

})